import { useEffect, useState } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, TransactionType } from 'vopay';
import { Title, Button, UtilButton, DropdownMenu } from 'UI';
import useAccountBearer from '@/components/Entities/hooks/useApi/useAccountBearer';
import useFetchAccountTransaction from '@/components/Entities/hooks/useApi/useFetchAccountTransaction';
import useFileDownload from '@/hooks/useFiledDownload';
import { useToast } from '@/providers/Toast.provider';
import EftFundingTransactionContainer from './EftFundingTransactionContainer';
import EftWithdrawTransactionContainer from './EftWithdrawTransactionContainer';
import InteracMoneyRequestTransactionContainer from './InteracMoneyRequestTransactionContainer';
import InteracBulkPayoutTransactionContainer from './InteracBulkPayoutTransactionContainer';
import CreditCardTransactionContainer from './CreditCardTransactionContainer';
import GenericTransactionContainer from './GenericTransactionContainer';
import TransactionRefundButton from './TransactionRefundButton';
import styles from './Transaction.module.scss';

/** The rails with a dedicated container below; any other rail renders the generic view. */
const RAILS_WITH_OWN_VIEW: ReadonlySet<string> = new Set([
  TransactionType.EFT_FUNDING,
  TransactionType.EFT_WITHDRAWAL,
  TransactionType.INTERACT_MONEY_REQUEST,
  TransactionType.INTERACT_BULK_PAYOUT,
  TransactionType.CREDIT_CARD,
]);

/**
 * `/accounts/:accountId/:transType/transaction/:TransactionID` — one of an account's transactions, read
 * through that account's bearer. `type` and `accountName` ride on the query as the list wrote them;
 * `date` (the row's own date) is what lets the generic view and the Refund button read the row back.
 */
export default function TransactionPage() {
  const { t } = useTranslation('translations', { keyPrefix: 'Transaction.Details' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { printFile, downloadPdfPage } = useFileDownload();
  const history = useHistory();
  const { accountId, TransactionID } = useParams<{ accountId: string; TransactionID: string }>();
  const query = new URLSearchParams(useLocation().search);
  const type = query.get('type') || '';
  const date = query.get('date');
  const { bearer, hasSettled: hasBearerSettled } = useAccountBearer(accountId);
  const toast = useToast();
  const { transaction, fetchAccountTransaction } = useFetchAccountTransaction();
  // The hook leaves its row untouched when the read throws, which would hold the view on its skeleton;
  // a failed read is resolved here to "no row", with the toast below carrying the reason.
  const [hasLookupFailed, setHasLookupFailed] = useState(false);

  /**
   * The row as the account's transaction list holds it, read once for the page: the generic view
   * renders it, and the Refund button filters on it. Rails with their own view fetch their richer
   * record themselves; this is what tells the button the status and whether a refund already ran.
   */
  useEffect(() => {
    if (!bearer || !date) return;

    setHasLookupFailed(false);

    fetchAccountTransaction(TransactionID, date, bearer).catch((error) => {
      setHasLookupFailed(true);

      // The hook has logged the cause; without the row the generic view says so and Refund stays hidden.
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      toast.neutral(
        t('GenericTransaction.loadError', {
          defaultValue: 'Could not load the transaction: {{errorMessage}}',
          errorMessage,
        }),
      );
    });
  }, [TransactionID, date, bearer]);

  const refreshTransaction = async () => (bearer && date ? fetchAccountTransaction(TransactionID, date, bearer) : null);

  /**
   * Nothing can be read when the link carries no date (an older link, before the list wrote one) or
   * the account's token could not be resolved — the lookup never starts in either case — and a read
   * that failed comes to the same thing. The view is handed null and says so, rather than waiting.
   */
  const isRowUnavailable = !date || hasLookupFailed || (hasBearerSettled && !bearer);

  const title = t('transactionOverview', { defaultValue: 'Transaction Overview' });

  return (
    <div className={styles.wrapper}>
      <Box flx="col" gapSize="16px">
        <UtilButton
          iconLeft="chevron_left"
          size="large"
          onClick={() => {
            history.goBack();
          }}
        >
          {tGlobal('Buttons.back', { defaultValue: 'Back' })}
        </UtilButton>

        <Box width="100%" flx="row" jc="sb">
          <Title>{t('transactionOverview', { defaultValue: 'Transaction Overview' })}</Title>

          <Box gapSize="12px">
            <TransactionRefundButton transaction={transaction} onRefunded={refreshTransaction} />

            <DropdownMenu>
              <DropdownMenu.Trigger asChild>
                <Button iconRight="keyboard_arrow_down">
                  {tGlobal('ExportDropdown.label', { defaultValue: 'Export' })}
                </Button>
              </DropdownMenu.Trigger>

              <DropdownMenu.Content align="end">
                <DropdownMenu.Item
                  onSelect={() => {
                    downloadPdfPage('transaction-overview', title);
                  }}
                >
                  {tGlobal('ExportDropdown.Options.pdf', { defaultValue: 'PDF' })}
                </DropdownMenu.Item>

                <DropdownMenu.Item
                  onSelect={() => {
                    printFile('transaction-overview');
                  }}
                >
                  {tGlobal('ExportDropdown.Options.print', { defaultValue: 'Print' })}
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu>
          </Box>
        </Box>
      </Box>

      <div id="transaction-overview" className={styles.overviewWrapper}>
        {type === TransactionType.EFT_FUNDING && (
          <EftFundingTransactionContainer TransactionID={TransactionID} Type={type} />
        )}

        {type === TransactionType.EFT_WITHDRAWAL && (
          <EftWithdrawTransactionContainer TransactionID={TransactionID} Type={type} />
        )}

        {type === TransactionType.INTERACT_MONEY_REQUEST && (
          <InteracMoneyRequestTransactionContainer TransactionID={TransactionID} Type={type} />
        )}

        {type === TransactionType.INTERACT_BULK_PAYOUT && (
          <InteracBulkPayoutTransactionContainer TransactionID={TransactionID} Type={type} />
        )}

        {type === TransactionType.CREDIT_CARD && (
          <CreditCardTransactionContainer TransactionID={TransactionID} Type={type} />
        )}

        {!RAILS_WITH_OWN_VIEW.has(type) && (
          <GenericTransactionContainer transaction={isRowUnavailable ? null : transaction} />
        )}
      </div>
    </div>
  );
}
